import { Injectable } from "@angular/core";
import { Origin, isOfType } from "../../../../models/types/general-types";
import { AccountView } from "../../../../models/view/account/account.view";
import { AccountStoreModel } from "../../../../models/store/account.store.model";
import { AccountType } from "../../../../models/types/account.types";
import { SourceAccountStoreModel } from "../../../../models/store/source-account-store.model";
import { SourceAccountView } from "../../../../models/view/source-book/source-account.view";
import { InstitutionView } from "../../../../models/view/institution/institution.view";
import { InstitutionAccountView } from "../../../../models/view/institution/institution-account.view";
import { AccountTypes } from "../../../../models/enum/account.enum";
import {
  getDefaultAccount,
  getOtherInstitutionId,
} from "../../../../shared/utils/helper.institution/default-institution-account";
import {
  AllowedCurrencies,
  DefaultCurrencyPerLocation,
  DefaultSystemCurrency,
  type CurrencyType,
} from "../../../../models/types/location-currency.types";
import { DefaultInstitutionStoreModel } from "../../../../models/store/institution.store.model";
import { PlaidActions } from "./plaid.actions";
import type { GlossPlaidAccountsResponse, PlaidAccount } from "../plaid.types";

/**
 * Placeholder institution id used when Plaid returns an account with no
 * resolvable institution. Scoped to Plaid (the "-US" suffix) so it cannot
 * collide with another provider's placeholder.
 */
const PLAID_UNKNOWN_INSTITUTION_ID = getOtherInstitutionId("US");

function plaidTypeToAccountType(type: string, subtype?: string): string {
  if (subtype === "credit card") {
    return AccountTypes.creditCard;
  }
  if (subtype === "savings") {
    return AccountTypes.savings;
  }
  if (subtype === "mortgage") {
    return AccountTypes.mortgage;
  }

  const map: Record<string, string> = {
    depository: AccountTypes.depository,
    credit: AccountTypes.creditCard,
    loan: AccountTypes.loan,
    investment: AccountTypes.investment,
    other: AccountTypes.transaction,
  };
  return map[type] ?? AccountTypes.transaction;
}

@Injectable({
  providedIn: "root",
})
export class PlaidAccountAction extends PlaidActions {
  /**
   * Ensures vault has an InstitutionView for the institution returned by the accounts endpoint.
   * Call before saving Plaid accounts so instLnk can resolve.
   */
  async ensurePlaidInstitutionInVault(response: GlossPlaidAccountsResponse): Promise<void> {
    const institutionId = response.institution_id;
    if (!institutionId) {
      return;
    }

    const vaultInstitutions = this.userStore.institutions.institutionViews() ?? [];
    const exists = vaultInstitutions.some((v) => v.instoPlaidId === institutionId);
    if (exists) {
      return;
    }

    const institution = this.createPlaidInstitution(institutionId, response.institution_name);
    await this.userStore.institutions.saveToVault([institution], true);
  }

  private createPlaidInstitution(plaidInstitutionId: string, name?: string): InstitutionView {
    // Plaid is US-only at this stage, so the locale fields are hardcoded to US.
    // If multi-region Plaid is added, thread userLocation through as Fiskil does.
    const institution = new InstitutionView({
      ...DefaultInstitutionStoreModel,
      id: crypto.randomUUID(),
      name: name ?? "Unknown",
      dc: new Date().toISOString(),
      dm: new Date().toISOString(),
      fk: "",
      fskId: "",
      plaidId: plaidInstitutionId,
      swt: { ...DefaultInstitutionStoreModel.swt, cc: "US", lc: "US" },
    });
    institution.availableAccounts[0] = getDefaultAccount(
      institution.id,
      DefaultCurrencyPerLocation.US,
    );
    return institution;
  }

  async getInstitutionForAccount(institutionId?: string): Promise<InstitutionView> {
    const institutions = this.userStore.institutions.institutionViews() ?? [];

    if (!institutionId) {
      return this.getOrCreateUnknownPlaceholderInstitution(institutions);
    }

    const matched = institutions.find((inst) => inst.instoPlaidId === institutionId);
    if (matched) {
      return matched;
    }

    return this.getOrCreateUnknownPlaceholderInstitution(institutions);
  }

  /** Last resort when institution_id is missing or not found. */
  private async getOrCreateUnknownPlaceholderInstitution(
    institutions: InstitutionView[],
  ): Promise<InstitutionView> {
    const defaultInst = institutions.find((inst) => inst.id === PLAID_UNKNOWN_INSTITUTION_ID);
    if (defaultInst) {
      return defaultInst;
    }

    const defaultInstitution = new InstitutionView({
      ...DefaultInstitutionStoreModel,
      id: PLAID_UNKNOWN_INSTITUTION_ID,
      name: "Other",
      swt: { ...DefaultInstitutionStoreModel.swt, cc: "US", lc: "US" },
    });
    defaultInstitution.availableAccounts[0] = getDefaultAccount(
      PLAID_UNKNOWN_INSTITUTION_ID,
      DefaultCurrencyPerLocation.US,
    );
    await this.userStore.institutions.saveToVault([defaultInstitution], true);
    return defaultInstitution;
  }

  toAccountStoreModel(
    plaidAccount: PlaidAccount,
    institution: InstitutionView,
    accountProduct: InstitutionAccountView,
  ): AccountStoreModel {
    return {
      dc: new Date().toISOString(),
      dm: new Date().toISOString(),
      id: crypto.randomUUID(),
      v: 3,
      rtInstLnk: null,
      vid: "",
      instLnk: {
        institutionId: institution?.id,
        institutionAccountId: accountProduct?.id,
      },
      curr: this.toCurrencyType(plaidAccount.balances.iso_currency_code),
      name: plaidAccount.name ?? "Account",
      crLm: plaidAccount.balances.limit ?? 0,
      bal: 0,
      tz: "",
      dfltCla: [],
      dfltCat: [],
      ori: Origin.plaid,
      bsqId: plaidAccount.account_id,
      date: new Date().toISOString(),
      type: plaidTypeToAccountType(plaidAccount.type, plaidAccount.subtype) as AccountType,
      st: [],
    };
  }

  toSourceAccountStoreModel(
    plaidAccount: PlaidAccount,
    accountStoreModel: AccountStoreModel,
  ): SourceAccountStoreModel {
    return {
      src: plaidAccount.name ?? "Account",
      acId: accountStoreModel.id,
      org: Origin.plaid,
      bsqId: plaidAccount.account_id,
      v: 1,
      vid: "",
      dc: new Date().toISOString(),
      dm: new Date().toISOString(),
      id: crypto.randomUUID(),
    };
  }

  /**
   * Source-book row for a Plaid-linked vault account. Reuses cipher id when a row
   * already exists for this account (by vault id or Plaid account_id) so sync updates
   * label + timestamps. Also reuses orphaned source rows left by interrupted imports,
   * preferring a match on the orphan's stored Plaid account_id over label alone once
   * that id is available (see buildPlaidSourceAccountView's orphan-matching logic).
   *
   * Orphans claimed earlier in the same import run are tracked in `claimedOrphanIds`
   * (#4181): the sync loop passes one shared Set across every iteration, so a second
   * Plaid account sharing a label with the first cannot reuse the already-claimed
   * orphan. The Set is filtered in at selection time only — the #3491 strictness probe
   * still runs against the full label-matched population, so claiming one orphan can
   * never shrink that population and flip a later account into the legacy label-only path.
   */
  buildPlaidSourceAccountView(
    plaidAccount: PlaidAccount,
    accountView: AccountView,
    existingSourceBooks: SourceAccountView[],
    existingAccounts: AccountView[] = [],
    claimedOrphanIds: Set<string> = new Set<string>(),
    allAccounts: AccountView[],
  ): SourceAccountView {
    const base = this.toSourceAccountStoreModel(plaidAccount, accountView.toStoreModel());
    const sourceLabel = plaidAccount.name ?? "Account";

    const existing = existingSourceBooks.find((s) => {
      if (s.origin !== Origin.plaid) {
        return false;
      }
      if (s.accountId === accountView.id) {
        return true;
      }
      const linked = existingAccounts.find((a) => a.id === s.accountId);
      return linked?.origin === Origin.plaid && linked.basiqAccountId === plaidAccount.account_id;
    });

    if (existing) {
      const existingModel = existing.toStoreModel();
      return new SourceAccountView({
        ...base,
        id: existingModel.id,
        vid: existingModel.vid,
        dc: existingModel.dc,
      });
    }

    // "Unclaimed by anyone" must be checked against the FULL account list (personal + org), not
    // the personal-only `existingAccounts` used above — a source row already linked to a real
    // org account isn't orphaned just because this personal refresh's candidate pool excludes
    // org accounts. Using the filtered list here would let this personal sync silently steal
    // (and overwrite) an org-owned source-account cipher's id/vid on a label collision.
    const labelMatchedOrphans = existingSourceBooks.filter(
      (s) =>
        s.origin === Origin.plaid &&
        !allAccounts.some((a) => a.id === s.accountId) &&
        s.source === sourceLabel,
    );

    // Disambiguate cross-institution collisions (#3491): an orphan created after this fix
    // carries the Plaid account_id it was originally meant for (see toSourceAccountStoreModel),
    // even though its own vault account never persisted. If any label match has that marker,
    // only a bsqId match may be reused — otherwise a same-named account at a different
    // institution could silently claim it. Orphans created before this fix have no bsqId to
    // check; when none of the label matches carry one, fall back to the old label-only match so
    // that legacy data keeps its existing (imperfect) recovery behavior.
    const bsqIdMatchedOrphans = labelMatchedOrphans.filter(
      (s) => s.bsqId != null && s.bsqId === plaidAccount.account_id,
    );
    const candidateOrphans = (
      labelMatchedOrphans.some((s) => s.bsqId != null) ? bsqIdMatchedOrphans : labelMatchedOrphans
    ).filter((s) => !claimedOrphanIds.has(s.id));
    const orphan = candidateOrphans.length === 1 ? candidateOrphans[0] : undefined;
    if (orphan) {
      const orphanModel = orphan.toStoreModel();
      claimedOrphanIds.add(orphan.id);
      return new SourceAccountView({
        ...base,
        id: orphanModel.id,
        vid: orphanModel.vid,
        dc: orphanModel.dc,
      });
    }

    return new SourceAccountView(base);
  }

  getAccountProduct(
    _plaidAccount: PlaidAccount,
    institution: InstitutionView,
  ): InstitutionAccountView {
    // Plaid institutions carry a single default "Other" product (see
    // createPlaidInstitution), so there is no subtype-named product to match —
    // always use the default entry.
    return (
      institution?.availableAccounts?.[0] ??
      getDefaultAccount(
        institution?.id ?? PLAID_UNKNOWN_INSTITUTION_ID,
        DefaultCurrencyPerLocation.US,
      )
    );
  }

  private toCurrencyType(raw: string | null | undefined): CurrencyType {
    return raw != null && isOfType<CurrencyType>(raw, AllowedCurrencies)
      ? raw
      : DefaultSystemCurrency;
  }
}
